import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import API from "../api";

function Chat() {
  const { conversationId } = useParams();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [contact, setContact] = useState(null);
  const [seen, setSeen] = useState(false);

  const [showMembers, setShowMembers] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [showRename, setShowRename] = useState(false);

  const [allMembers, setAllMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [newName, setNewName] = useState("");

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const touchStartX = useRef(0);

  useEffect(() => {
    socketRef.current = io("https://api.joss-serveur.duckdns.org");
    socketRef.current.emit("register", user.id);

    return () => {
      socketRef.current?.disconnect();
    };
  }, [user.id]);

  useEffect(() => {
    fetchContact();
  }, [conversationId]);

  useEffect(() => {
    fetchAllMembers();
  }, []);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await API.get(`/messages/${conversationId}`);
        setMessages(res.data);

        await API.post(`/conversations/${conversationId}/read`);
        await fetchSeenStatus();
      } catch (err) {
        console.error(err);
      }
    };

    fetchMessages();

    if (!socketRef.current) return;

    socketRef.current.emit("join", conversationId);

    const handleNewMessage = (msg) => {
      if (Number(msg.conversation_id) !== Number(conversationId)) return;

      setMessages((prev) => [...prev, msg]);

      if (Number(msg.user_id) === Number(user.id)) {
        setSeen(false);
      } else {
        API.post(`/conversations/${conversationId}/read`);
      }
    };

    const handleMessagesRead = () => {
      fetchSeenStatus();
    };

    socketRef.current.on("newMessage", handleNewMessage);
    socketRef.current.on("messagesRead", handleMessagesRead);

    return () => {
      socketRef.current?.off("newMessage", handleNewMessage);
      socketRef.current?.off("messagesRead", handleMessagesRead);
    };
  }, [conversationId, user.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchContact = async () => {
    try {
      const res = await API.get(`/conversations/${conversationId}`);
      setContact(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSeenStatus = async () => {
    try {
      const res = await API.get(`/conversations/${conversationId}/seen-status`);
      setSeen(res.data.seen);
    } catch (err) {
      setSeen(false);
    }
  };

  const fetchAllMembers = async () => {
    try {
      const res = await API.get("/members");
      setAllMembers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const sendMessage = () => {
    if (!content.trim()) return;
    if (!socketRef.current) return;

    socketRef.current.emit("sendMessage", {
      conversationId,
      userId: user.id,
      content: content.trim(),
    });

    setContent("");
  };

  const toggleSelectedMember = (memberId) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const addMembers = async () => {
    if (selectedMembers.length === 0) {
      alert("Choisis au moins un membre");
      return;
    }

    try {
      await API.post(`/conversations/${conversationId}/members`, {
        userIds: selectedMembers,
      });

      setSelectedMembers([]);
      setShowAddMembers(false);
      await fetchContact();
    } catch (err) {
      alert(err?.response?.data?.message || "Erreur ajout membre");
    }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm("Retirer ce membre du groupe ?")) return;

    try {
      await API.delete(`/conversations/${conversationId}/members/${memberId}`);
      await fetchContact();
    } catch (err) {
      alert(err?.response?.data?.message || "Erreur suppression membre");
    }
  };

  const openRenameModal = () => {
    setNewName(contact?.name || "");
    setShowRename(true);
  };

  const renameGroup = async () => {
    if (!newName.trim()) {
      alert("Nom du groupe obligatoire");
      return;
    }

    try {
      await API.put(`/conversations/${conversationId}/name`, {
        name: newName.trim(),
      });

      setShowRename(false);
      setNewName("");
      await fetchContact();
    } catch (err) {
      alert(err?.response?.data?.message || "Erreur renommage");
    }
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta > 100) navigate(-1);
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isSameDay = (a, b) => {
    const da = new Date(a);
    const db = new Date(b);

    return (
      da.getDate() === db.getDate() &&
      da.getMonth() === db.getMonth() &&
      da.getFullYear() === db.getFullYear()
    );
  };

  const formatDateSeparator = (date) => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date();

    yesterday.setDate(today.getDate() - 1);

    if (isSameDay(d, today)) return "Aujourd’hui";
    if (isSameDay(d, yesterday)) return "Hier";

    return d.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const isPublic = contact?.type === "public";
  const isGroup = contact?.type === "group";
  const isGroupLike = isGroup || isPublic;

  const isOwner = isGroup && Number(contact?.owner_id) === Number(user?.id);

  const title = isPublic
    ? contact?.name || "Salon de la loge"
    : isGroup
    ? contact?.name?.trim() || "Groupe sans nom"
    : contact
    ? `${contact.firstname || ""} ${contact.lastname || ""}`.trim()
    : "Chargement...";

  const subtitle = isPublic
    ? `${contact?.members?.length || 0} membres · Salon public`
    : isGroup
    ? `${contact?.members?.length || 0} membres${isOwner ? " · Admin" : ""}`
    : "Conversation privée";

  const initials = isPublic
    ? "🏛️"
    : isGroup
    ? "👥"
    : contact
    ? `${contact.firstname?.[0] || ""}${contact.lastname?.[0] || ""}`.toUpperCase()
    : "?";

  const getSenderName = (msg) => {
    if (!isGroupLike) return "";
    if (Number(msg.user_id) === Number(user.id)) return "";

    return `${msg.firstname || ""} ${msg.lastname || ""}`.trim() || "Membre";
  };

  const availableMembersToAdd = allMembers.filter((m) => {
    const alreadyInGroup = contact?.members?.some(
      (cm) => Number(cm.id) === Number(m.id)
    );

    return !alreadyInGroup;
  });

  return (
    <div
      style={styles.container}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div style={styles.goldOrbOne} />
      <div style={styles.goldOrbTwo} />

      <div style={styles.header}>
        <button onClick={() => navigate(-1)} style={styles.backBtn}>
          ←
        </button>

        <div
          style={{
            ...styles.contactBlock,
            cursor: isGroupLike ? "pointer" : "default",
          }}
          onClick={() => {
            if (isGroupLike) setShowMembers(true);
          }}
        >
          <div style={styles.avatar}>{initials}</div>

          <div style={styles.contactText}>
            <div style={styles.nameRow}>
              <div style={styles.name}>{title}</div>

              {isGroup && isOwner && (
                <button
                  style={styles.renameBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    openRenameModal();
                  }}
                >
                  ✏️
                </button>
              )}
            </div>

            <div style={styles.status}>
              <span style={styles.statusDot} />
              {subtitle}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.messages}>
        {messages.map((msg, index) => {
          const isMe = Number(msg.user_id) === Number(user.id);
          const previousMsg = messages[index - 1];

          const showDate =
            !previousMsg || !isSameDay(previousMsg.created_at, msg.created_at);

          const isLastMessage = index === messages.length - 1;

          const showSeen =
            contact?.type === "private" &&
            isMe &&
            isLastMessage;

          return (
            <div key={msg.id || index}>
              {showDate && (
                <div style={styles.dateSeparator}>
                  {formatDateSeparator(msg.created_at)}
                </div>
              )}

              <div
                style={{
                  ...styles.messageRow,
                  justifyContent: isMe ? "flex-end" : "flex-start",
                }}
              >
                <div
                  style={{
                    ...styles.bubble,
                    ...(isMe ? styles.myBubble : styles.otherBubble),
                  }}
                >
                  {!isMe && isGroupLike && (
                    <div style={styles.senderName}>{getSenderName(msg)}</div>
                  )}

                  <div style={styles.messageText}>{msg.content}</div>
                  <div style={styles.time}>{formatTime(msg.created_at)}</div>

                  {showSeen && (
                    <div style={styles.seen}>
                      {seen ? "Vu" : "Envoyé"}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      <div style={styles.inputArea}>
        <div style={styles.inputBox}>
          <input
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Écrire un message..."
            style={styles.input}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          />

          <button onClick={sendMessage} style={styles.sendBtn}>
            ➤
          </button>
        </div>
      </div>

      {showMembers && isGroupLike && (
        <div style={styles.modalOverlay} onClick={() => setShowMembers(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>{contact?.name}</h2>
                <p style={styles.modalSubtitle}>
                  {contact?.members?.length || 0} membres
                  {isOwner ? " · Vous êtes admin" : ""}
                </p>
              </div>

              <button style={styles.closeBtn} onClick={() => setShowMembers(false)}>
                ×
              </button>
            </div>

            {isOwner && (
              <>
                <button
                  style={styles.addMemberBtn}
                  onClick={() => {
                    setSelectedMembers([]);
                    setShowAddMembers(true);
                  }}
                >
                  ➕ Ajouter un membre
                </button>

                <button style={styles.renameLargeBtn} onClick={openRenameModal}>
                  ✏️ Renommer le groupe
                </button>
              </>
            )}

            <div style={styles.membersList}>
              {contact?.members?.map((member) => (
                <div key={member.id} style={styles.memberItem}>
                  <div style={styles.memberAvatar}>
                    {(member.firstname?.[0] || "").toUpperCase()}
                    {(member.lastname?.[0] || "").toUpperCase()}
                  </div>

                  <div style={styles.memberText}>
                    <strong>
                      {member.firstname} {member.lastname}
                    </strong>

                    <p style={styles.memberRole}>
                      {member.role}
                      {Number(member.id) === Number(contact?.owner_id)
                        ? " · Admin du groupe"
                        : ""}
                    </p>
                  </div>

                  {isOwner && Number(member.id) !== Number(user.id) && (
                    <button
                      style={styles.removeBtn}
                      onClick={() => removeMember(member.id)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showAddMembers && isGroup && isOwner && (
        <div style={styles.modalOverlay} onClick={() => setShowAddMembers(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>Ajouter des membres</h2>
                <p style={styles.modalSubtitle}>
                  {selectedMembers.length} sélectionné
                  {selectedMembers.length > 1 ? "s" : ""}
                </p>
              </div>

              <button style={styles.closeBtn} onClick={() => setShowAddMembers(false)}>
                ×
              </button>
            </div>

            <div style={styles.membersList}>
              {availableMembersToAdd.length === 0 ? (
                <p style={styles.emptyText}>
                  Tous les membres sont déjà dans ce groupe.
                </p>
              ) : (
                availableMembersToAdd.map((member) => {
                  const selected = selectedMembers.includes(member.id);

                  return (
                    <div
                      key={member.id}
                      style={{
                        ...styles.memberItem,
                        ...(selected ? styles.memberItemSelected : {}),
                      }}
                      onClick={() => toggleSelectedMember(member.id)}
                    >
                      <div style={styles.memberAvatar}>
                        {(member.firstname?.[0] || "").toUpperCase()}
                        {(member.lastname?.[0] || "").toUpperCase()}
                      </div>

                      <div style={styles.memberText}>
                        <strong>
                          {member.firstname} {member.lastname}
                        </strong>
                        <p style={styles.memberRole}>{member.role}</p>
                      </div>

                      <div style={selected ? styles.checkSelected : styles.check}>
                        {selected ? "✓" : ""}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <button style={styles.validateBtn} onClick={addMembers}>
              Ajouter
            </button>
          </div>
        </div>
      )}

      {showRename && isGroup && isOwner && (
        <div style={styles.modalOverlay} onClick={() => setShowRename(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>Renommer le groupe</h2>
                <p style={styles.modalSubtitle}>Nouveau nom du groupe</p>
              </div>

              <button style={styles.closeBtn} onClick={() => setShowRename(false)}>
                ×
              </button>
            </div>

            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nom du groupe"
              style={styles.inputRename}
              autoFocus
            />

            <button style={styles.validateBtn} onClick={renameGroup}>
              Valider
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Chat;

const gold = "#d6b46a";

const styles = {
  container: {
    position: "relative",
    height: "100%",
    minHeight: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    background: `
      radial-gradient(circle at 50% 16%, rgba(214,180,106,0.18), transparent 34%),
      radial-gradient(circle at 85% 70%, rgba(214,180,106,0.10), transparent 38%),
      linear-gradient(180deg, #050815 0%, #02040a 100%)
    `,
    color: "white",
    overflow: "hidden",
  },

  goldOrbOne: {
    position: "absolute",
    top: 90,
    right: -70,
    width: 180,
    height: 180,
    borderRadius: "50%",
    background: "rgba(214,180,106,0.08)",
    filter: "blur(45px)",
    pointerEvents: "none",
  },

  goldOrbTwo: {
    position: "absolute",
    bottom: 170,
    left: -80,
    width: 180,
    height: 180,
    borderRadius: "50%",
    background: "rgba(214,180,106,0.06)",
    filter: "blur(50px)",
    pointerEvents: "none",
  },

  header: {
    position: "relative",
    zIndex: 2,
    height: 78,
    flexShrink: 0,
    padding: "10px 16px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: 12,
    borderBottom: "1px solid rgba(214,180,106,0.18)",
    background:
      "linear-gradient(180deg, rgba(2,6,23,0.98), rgba(2,6,23,0.82))",
    boxShadow: "0 12px 34px rgba(0,0,0,0.36)",
    backdropFilter: "blur(14px)",
  },

  backBtn: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    border: "1px solid rgba(214,180,106,0.32)",
    background:
      "linear-gradient(145deg, rgba(214,180,106,0.12), rgba(255,255,255,0.03))",
    color: gold,
    fontSize: 24,
    fontWeight: 900,
    flexShrink: 0,
  },

  contactBlock: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
    flex: 1,
    overflow: "hidden",
  },

  avatar: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    flexShrink: 0,
    background:
      "radial-gradient(circle at 30% 20%, rgba(255,240,180,0.42), rgba(214,180,106,0.18) 45%, rgba(10,15,20,0.9) 100%)",
    border: "1px solid rgba(214,180,106,0.52)",
    color: gold,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950,
    fontSize: 17,
  },

  contactText: {
    minWidth: 0,
    flex: 1,
    overflow: "hidden",
  },

  nameRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
  },

  name: {
    flex: 1,
    minWidth: 0,
    fontSize: 18,
    fontWeight: 950,
    color: "white",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  renameBtn: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    border: "1px solid rgba(214,180,106,0.32)",
    background: "rgba(214,180,106,0.12)",
    color: gold,
    fontSize: 16,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },

  status: {
    marginTop: 3,
    fontSize: 12,
    color: "rgba(255,255,255,0.50)",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },

  statusDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: gold,
    boxShadow: "0 0 10px rgba(214,180,106,0.75)",
    display: "inline-block",
  },

  messages: {
    position: "relative",
    zIndex: 1,
    flex: 1,
    overflowY: "auto",
    padding: "14px 14px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  dateSeparator: {
    alignSelf: "center",
    margin: "10px auto 8px",
    padding: "6px 12px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(214,180,106,0.18)",
    color: "rgba(255,255,255,0.68)",
    fontSize: 12,
    fontWeight: 800,
    width: "fit-content",
  },

  messageRow: {
    display: "flex",
    width: "100%",
  },

  bubble: {
    maxWidth: "78%",
    padding: "10px 13px 8px",
    borderRadius: 19,
    fontSize: 15,
    lineHeight: 1.35,
    backdropFilter: "blur(8px)",
  },

  myBubble: {
    background: "linear-gradient(145deg, #f0d98a, #b88a2f)",
    color: "#050815",
    borderBottomRightRadius: 6,
    border: "1px solid rgba(255,255,255,0.16)",
  },

  otherBubble: {
    background:
      "linear-gradient(145deg, rgba(255,255,255,0.09), rgba(255,255,255,0.045))",
    border: "1px solid rgba(255,255,255,0.10)",
    color: "rgba(255,255,255,0.94)",
    borderBottomLeftRadius: 6,
  },

  senderName: {
    marginBottom: 4,
    fontSize: 12,
    fontWeight: 900,
    color: gold,
  },

  messageText: {
    wordBreak: "break-word",
  },

  time: {
    marginTop: 5,
    fontSize: 10,
    opacity: 0.56,
    textAlign: "right",
  },

  seen: {
    marginTop: 3,
    fontSize: 10,
    opacity: 0.72,
    textAlign: "right",
    fontWeight: 800,
  },

  inputArea: {
    position: "relative",
    zIndex: 3,
    flexShrink: 0,
    padding: "10px 14px 110px",
    background:
      "linear-gradient(180deg, rgba(2,4,10,0), rgba(2,4,10,0.72))",
  },

  inputBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 6,
    borderRadius: 999,
    background: "rgba(10,15,20,0.72)",
    border: "1px solid rgba(214,180,106,0.36)",
  },

  input: {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    color: "white",
    padding: "12px 14px",
    fontSize: 15,
  },

  sendBtn: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    border: "none",
    background: "linear-gradient(145deg, #f0d98a, #b88a2f)",
    color: "#050815",
    fontSize: 20,
    fontWeight: 950,
    flexShrink: 0,
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(0,0,0,0.62)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  modal: {
    width: "92%",
    maxHeight: "78vh",
    overflowY: "auto",
    borderRadius: 28,
    background:
      "linear-gradient(180deg, rgba(10,15,25,0.98), rgba(2,4,10,0.98))",
    border: "1px solid rgba(214,180,106,0.25)",
    padding: "20px 18px 30px",
    boxShadow: "0 -18px 50px rgba(0,0,0,0.55)",
  },

  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 18,
  },

  modalTitle: {
    margin: 0,
    fontSize: 24,
    fontWeight: 950,
  },

  modalSubtitle: {
    margin: "4px 0 0",
    color: "rgba(255,255,255,0.55)",
    fontSize: 14,
  },

  closeBtn: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    border: "1px solid rgba(214,180,106,0.32)",
    background: "rgba(214,180,106,0.1)",
    color: gold,
    fontSize: 28,
    fontWeight: 900,
  },

  addMemberBtn: {
    width: "100%",
    marginBottom: 10,
    border: "none",
    borderRadius: 16,
    padding: "13px 16px",
    background: "linear-gradient(145deg, #f0d98a, #b88a2f)",
    color: "#050815",
    fontSize: 15,
    fontWeight: 950,
  },

  renameLargeBtn: {
    width: "100%",
    marginBottom: 14,
    border: "1px solid rgba(214,180,106,0.35)",
    borderRadius: 16,
    padding: "13px 16px",
    background: "rgba(214,180,106,0.10)",
    color: gold,
    fontSize: 15,
    fontWeight: 950,
  },

  membersList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  memberItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 12,
    borderRadius: 18,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(214,180,106,0.12)",
  },

  memberItemSelected: {
    border: "1px solid rgba(214,180,106,0.55)",
    background: "rgba(214,180,106,0.14)",
  },

  memberAvatar: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background:
      "radial-gradient(circle at top, rgba(214,180,106,0.48), rgba(214,180,106,0.10))",
    border: "1px solid rgba(214,180,106,0.45)",
    color: gold,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    flexShrink: 0,
  },

  memberText: {
    flex: 1,
    minWidth: 0,
  },

  memberRole: {
    margin: "3px 0 0",
    color: "rgba(255,255,255,0.5)",
    fontSize: 13,
  },

  removeBtn: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    border: "1px solid rgba(255,80,80,0.35)",
    background: "rgba(255,80,80,0.12)",
    color: "#ff6b6b",
    fontSize: 18,
    fontWeight: 900,
    flexShrink: 0,
  },

  validateBtn: {
    width: "100%",
    marginTop: 16,
    border: "none",
    borderRadius: 16,
    padding: "14px 16px",
    background: "linear-gradient(145deg, #f0d98a, #b88a2f)",
    color: "#050815",
    fontSize: 15,
    fontWeight: 950,
  },

  check: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "1px solid rgba(214,180,106,0.42)",
    flexShrink: 0,
  },

  checkSelected: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "linear-gradient(145deg, #f0d98a, #b88a2f)",
    color: "#050815",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950,
    flexShrink: 0,
  },

  inputRename: {
    width: "100%",
    padding: "14px 15px",
    borderRadius: 16,
    border: "1px solid rgba(214,180,106,0.4)",
    background: "rgba(0,0,0,0.3)",
    color: "white",
    fontSize: 16,
    outline: "none",
    boxSizing: "border-box",
  },

  emptyText: {
    margin: 0,
    color: "rgba(255,255,255,0.62)",
    textAlign: "center",
    padding: 20,
  },
};